//-- Copyright (c) 2021 TMT Insights Group, LLC
import { MouseEventHandler, useContext, useEffect, useState } from 'react';
import { createSelector } from '@reduxjs/toolkit';
import { format } from 'date-fns';
import { isEqual } from 'lodash';
import styled from '@emotion/styled';
import { Menu, TextField } from '@mui/material';

import { ConfigContext } from '@/contextAPI/configContext';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { applyFilter } from '@/redux/slices/dataTableSlice';
import { selectDataTableFilters } from '@/redux/selectors/dataTableSelectors';
import { selectUserPreferences } from '@/redux/selectors/authSelectors';
import { formatDate } from '@/_utils/date';
import FilterChip from '@/components/_common/Filters/FilterChip';
import DateRangepicker, { DateRangePickerValue } from '@/components/_common/Datepicker/DateRangepicker';
import FilterAccordion from '@/components/_common/Filters/FilterAccordion';

export interface FilterDateRangeProps {
  type: string;
  field?: string;
  label?: string;
  role?: ReturnType<typeof createSelector>;
  isQuickFilters: boolean;
  expandedFilters?: string[];
  onExpand?: (filter: string) => void;
  advanceFilters?: Record<string, any>;
  onAdvanceFiltersChange?: (newAdvanceFilters: { type: string; selections: any }) => void;
}

const FilterDateRange = ({
  type,
  field = 'due_date',
  label = 'Due Date',
  role,
  isQuickFilters,
  expandedFilters = [],
  onExpand,
  advanceFilters,
  onAdvanceFiltersChange
}: FilterDateRangeProps) => {
  const isFiltersV2 = useContext(ConfigContext)?.filtersVersion === 2;
  const hasPermissions = role ? useAppSelector(role) : true;
  const dispatch = useAppDispatch();
  const userPreferences = useAppSelector(selectUserPreferences);
  const filters = useAppSelector(selectDataTableFilters);
  const isAdvanceFilter = isFiltersV2 && !isQuickFilters;
  const isSelected = isAdvanceFilter ? advanceFilters?.[type] : filters[type];
  const isAccordionExpanded = expandedFilters.includes(type);
  const userPrefDateFormat = userPreferences.date_format ? userPreferences.date_format.replace('mm', 'MM') : 'MM/dd/yyyy';
  const [dateRange, setDateRange] = useState<{ start: Date | null; end: Date | null }>({
    start: isSelected?.[`${field}__gte`] ? new Date(isSelected[`${field}__gte`]) : null,
    end: isSelected?.[`${field}__lte`] ? new Date(isSelected[`${field}__lte`]) : null
  });

  let initialValue: DateRangePickerValue | undefined;
  if (isSelected) {
    initialValue = {
      endDate: new Date(isSelected[`${field}__lte`]),
      startDate: new Date(isSelected[`${field}__gte`]),
      key: 'selection'
    };
  }
  const [selectedRange, setSelectedRange] = useState<DateRangePickerValue | undefined>(initialValue);
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | HTMLInputElement | null>(null);

  if (!isEqual(initialValue, selectedRange)) {
    setSelectedRange(initialValue);
  }

  const handleClick: MouseEventHandler<HTMLDivElement | HTMLInputElement> = (event) => setAnchorEl(event.currentTarget);

  const handleClose = () => setAnchorEl(null);

  const handleClear: MouseEventHandler<HTMLButtonElement | HTMLElement> = (e) => {
    setSelectedRange(undefined);
    setDateRange({ start: null, end: null });
    const newValue = { type, selections: null };
    if (isAdvanceFilter && onAdvanceFiltersChange) {
      onAdvanceFiltersChange(newValue);
    } else {
      dispatch(applyFilter(newValue));
    }
    e.preventDefault();
    e.stopPropagation();
  };

  const getApplied = () => {
    if (!selectedRange || !isSelected) {
      return 'All';
    } else {
      return `${formatDate(`${selectedRange.startDate}`)} - ${formatDate(`${selectedRange.endDate}`)}`;
    }
  };

  const handleOnChange = (value?: DateRangePickerValue) => {
    handleClose();
    if (value) {
      setSelectedRange(value);
      const newValue = { type, selections: { [`${field}__gte`]: value.startDate, [`${field}__lte`]: value.endDate } };
      if (isAdvanceFilter && onAdvanceFiltersChange) {
        onAdvanceFiltersChange(newValue);
      } else {
        dispatch(applyFilter(newValue));
      }
    }
  };

  useEffect(
    function updateRangeOnAppliedFiltersChangedExternally() {
      setDateRange({
        start: isSelected?.[`${field}__gte`] ? new Date(isSelected[`${field}__gte`]) : null,
        end: isSelected?.[`${field}__lte`] ? new Date(isSelected[`${field}__lte`]) : null
      });
    },
    [isSelected]
  );

  if (!hasPermissions) return null;

  return isFiltersV2 && !isQuickFilters ? (
    <FilterAccordion
      expanded={isAccordionExpanded}
      label={label}
      showClear={Boolean(dateRange.start && dateRange.end)}
      onClear={handleClear}
      onChange={() => onExpand?.(type)}
    >
      <DateRangeWrapper>
        <DateRangeInput
          fullWidth
          value={
            selectedRange
              ? `${format(selectedRange?.startDate as Date, userPrefDateFormat)} ${selectedRange?.endDate ? `- ${format(selectedRange.endDate, userPrefDateFormat)}` : ''}`
              : `${label}...`
          }
          onClick={handleClick}
          InputProps={{ readOnly: true }}
        />
        <Menu
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleClose}
        >
          <DateRangepicker autoApplyOnChange value={selectedRange} onChange={handleOnChange} />
        </Menu>
      </DateRangeWrapper>
    </FilterAccordion>
  ) : (
    <div style={{ marginRight: 10 }}>
      <FilterChip
        label={
          <>
            {label}: <strong style={{ marginLeft: 5 }}>{getApplied()}</strong>
          </>
        }
        isSelected={isSelected}
        onClick={handleClick}
        onClear={handleClear}
      />
      <Menu
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
      >
        <DateRangepicker value={selectedRange} onChange={handleOnChange} />
      </Menu>
    </div>
  );
};

export default FilterDateRange;

const DateRangeWrapper = styled.div(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
  padding: theme.spacing(1, 0)
}));

const DateRangeInput = styled(TextField)(({ theme }) => ({
  '&:hover input': {
    cursor: 'pointer'
  },
  '& .MuiInputBase-root': {
    color: theme.palette.text.secondary,
    backgroundColor: theme.palette?.filters?.input?.background || (theme.palette.mode === 'dark' ? '#25272C' : '#f5f5f5'),
    '&.Mui-focused, &:hover': {
      backgroundColor: theme.palette?.filters?.input?.focused || (theme.palette.mode === 'dark' ? '#313339' : '#ffffff')
    }
  }
}));
